import React, { useEffect, useState } from "react";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { studentAuth } from "../../firebase";

const PASSWORD_RULES = [
  { key: "length", label: "8 to 15 characters", test: password => password.length >= 8 && password.length <= 15 },
  { key: "uppercase", label: "At least 1 capital letter", test: password => /[A-Z]/.test(password) },
  { key: "lowercase", label: "At least 1 small letter", test: password => /[a-z]/.test(password) },
  { key: "number", label: "At least 1 number", test: password => /\d/.test(password) },
  { key: "special", label: "At least 1 special character", test: password => /[^A-Za-z0-9]/.test(password) },
];

export default function StudentPasswordReset() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const actionCode = searchParams.get("oobCode");
    if (!actionCode) {
      setError("This password reset link is missing or invalid.");
      setLoading(false);
      return;
    }

    setCode(actionCode);
    verifyPasswordResetCode(studentAuth, actionCode)
      .then(setEmail)
      .catch(() => setError("This password reset link has expired or already been used."))
      .finally(() => setLoading(false));
  }, [searchParams]);

  const rules = PASSWORD_RULES.map(rule => ({ ...rule, valid: rule.test(newPassword) }));
  const passwordValid = rules.every(rule => rule.valid);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!passwordValid) {
      setError("Please meet all password requirements.");
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await confirmPasswordReset(studentAuth, code, newPassword);
      try {
        const response = await fetch("https://us-central1-enrollease-19c30.cloudfunctions.net/markStudentPasswordChanged", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        if (!response.ok) {
          console.warn("Password changed, but student status could not be updated:", await response.text());
        }
      } catch (recordError) {
        console.warn("Password changed, but student status could not be updated:", recordError);
      }
      setMessage("Password changed successfully. You can now sign in with your new password.");
      setTimeout(() => navigate("/signin", { replace: true }), 1600);
    } catch (resetError) {
      console.error("Student password reset failed:", resetError);
      setError(resetError?.code === "auth/expired-action-code" || resetError?.code === "auth/invalid-action-code"
        ? "This password reset link has expired or already been used."
        : resetError?.message || "Unable to change your password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "#eef5f0" }}>
      <div style={{ width: 480, maxWidth: "100%", background: "#fff", border: "1px solid #d8e5dd", borderTop: "6px solid #0f5f3b", padding: "32px 30px", borderRadius: 10, boxShadow: "0 12px 30px rgba(15,95,59,0.12)" }}>
        <div style={{ color: "#0f5f3b", fontSize: 24, fontWeight: 700 }}>EnrollEase</div>
        <div style={{ color: "#66756d", fontSize: 13, marginBottom: 24 }}>Manila Adventist College</div>
        <h2 style={{ margin: "0 0 10px", color: "#24322c" }}>Reset your password</h2>
        <p style={{ color: "#52625a", fontSize: 14, marginTop: 0, marginBottom: 24 }}>Create a new secure password for your EnrollEase account.</p>
        {loading ? (
          <div style={{ color: "#555" }}>Verifying your secure link...</div>
        ) : error && !email ? (
          <div>
            <div style={{ color: "#b00020", marginBottom: 16 }}>{error}</div>
            <button type="button" onClick={() => navigate("/signin")} style={{ padding: "10px 16px", background: "#0f5f3b", color: "#fff", border: 0, borderRadius: 8 }}>Back to sign in</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ color: "#555", fontSize: 14, marginBottom: 16 }}>Set a new password for <strong>{email}</strong>.</div>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 6, color: "#24322c" }}>New password</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={event => setNewPassword(event.target.value)} autoComplete="new-password" style={{ flex: 1, padding: 12, borderRadius: 8, border: "1px solid #d1d5db" }} />
              <button type="button" title={showNewPassword ? "Hide password" : "Show password"} aria-label={showNewPassword ? "Hide password" : "Show password"} onClick={() => setShowNewPassword(value => !value)} style={{ width: 46, border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0f5f3b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
                  {showNewPassword ? <path d="m4 4 16 16" /> : <circle cx="12" cy="12" r="2.5" />}
                </svg>
              </button>
            </div>
            <div style={{ background: "#f5faf6", border: "1px solid #d8e5dd", borderRadius: 8, padding: 14, marginBottom: 18 }}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: "#0f5f3b" }}>Password requirements</div>
              {rules.map(rule => <div key={rule.key} style={{ color: rule.valid ? "#18803b" : "#666", fontSize: 13, lineHeight: 1.7 }}>{rule.valid ? "✓" : "○"} {rule.label}</div>)}
            </div>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 6, color: "#24322c" }}>Confirm password</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} autoComplete="new-password" style={{ flex: 1, padding: 12, borderRadius: 8, border: "1px solid #d1d5db" }} />
              <button type="button" title={showConfirmPassword ? "Hide password" : "Show password"} aria-label={showConfirmPassword ? "Hide password" : "Show password"} onClick={() => setShowConfirmPassword(value => !value)} style={{ width: 46, border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0f5f3b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
                  {showConfirmPassword ? <path d="m4 4 16 16" /> : <circle cx="12" cy="12" r="2.5" />}
                </svg>
              </button>
            </div>
            {confirmPassword && <div style={{ color: passwordsMatch ? "#18803b" : "#b00020", fontSize: 13, marginBottom: 10 }}>{passwordsMatch ? "✓ Passwords match" : "Passwords do not match"}</div>}
            {error && <div style={{ color: "#b00020", marginBottom: 10 }}>{error}</div>}
            {message && <div style={{ color: "#18803b", marginBottom: 10 }}>{message}</div>}
            <button type="submit" disabled={saving || !passwordValid || !passwordsMatch} style={{ width: "100%", padding: 13, background: saving || !passwordValid || !passwordsMatch ? "#b8c2bd" : "#0f5f3b", color: "#fff", border: 0, borderRadius: 6, fontWeight: 700, cursor: saving || !passwordValid || !passwordsMatch ? "not-allowed" : "pointer" }}>{saving ? "Saving..." : "Reset password"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
